/**
 * Per-conversation state of a running chat request ("run").
 *
 * The chat used to keep a single set of refs for *the* running request, which
 * made a run the property of the component rather than of its conversation: a
 * request started in conversation A kept driving the UI after the user switched
 * to B — the progress dots stayed on screen, streamed chunks were written into
 * the conversation being displayed, and a second conversation could not be
 * started at all. Runs are keyed by conversation id here, so the dots belong to
 * the conversation that owns the run and several conversations can stream at
 * the same time.
 *
 * Entries survive the end of their run: the reconnect cursor and the replayable
 * request are what the "reconnect / replay" affordances read after a stream
 * failed, so `finishChatRun` clears only what is tied to the in-flight attempt.
 */

export type ChatRunReconnectCursor = { runId: string; resumeFromChunk: number };

export type ChatRunReplayRequest = {
    conversation: any;
    input: string;
    prompt?: string;
    exclusiveToolId?: string;
    /** Per @ im Eingabefeld genannte Werkzeuge — siehe `toolMentionText.ts`. */
    exclusiveToolIds?: string[];
};

export interface ChatRun {
    /** Conversation this run belongs to (`String(conversation._id)`). */
    conversationId: string;
    /** True while the request is in flight — the conversation's progress dots. */
    pending: boolean;
    /** Identifies the current request so late chunks of an older one are dropped. */
    requestId: number | null;
    /** Aborts the whole request (user stop, timeout). */
    abortController: AbortController | null;
    /** Why the request was aborted, when the reason is ours rather than the DOM's. */
    abortCause: string | null;
    /** Aborts only the current stream attempt (stall reconnect, manual reconnect). */
    attemptAbortController: AbortController | null;
    lastStreamActivity: number | null;
    /** Cursor of the attempt in flight. */
    reconnect: { runId: string | null; resumeFromChunk: number };
    /** Cursor kept for a manual reconnect after the attempt gave up. */
    savedCursor: ChatRunReconnectCursor | null;
    /** Conversation the saved cursor resumes into. */
    savedConversation: any | null;
    /** Last request of this conversation, replayable after a failure. */
    replayRequest: ChatRunReplayRequest | null;
    /** Whether this conversation currently offers "reconnect". */
    manualReconnectAvailable: boolean;
    /** Latest streamed conversation, so switching back shows the live state. */
    snapshot: any | null;
}

export type ChatRunMap = Map<string, ChatRun>;

/** Conversations are keyed by their string id; an unsaved one keys as ''. */
export const chatRunKey = (conversationId: unknown): string =>
    conversationId == null ? '' : String(conversationId);

export const createChatRun = (conversationId: string): ChatRun => ({
    conversationId,
    pending: false,
    requestId: null,
    abortController: null,
    abortCause: null,
    attemptAbortController: null,
    lastStreamActivity: null,
    reconnect: { runId: null, resumeFromChunk: 0 },
    savedCursor: null,
    savedConversation: null,
    replayRequest: null,
    manualReconnectAvailable: false,
    snapshot: null,
});

export const getChatRun = (runs: ChatRunMap, conversationId: unknown): ChatRun | undefined =>
    runs.get(chatRunKey(conversationId));

/** The run of this conversation, created on first use. */
export const ensureChatRun = (runs: ChatRunMap, conversationId: unknown): ChatRun => {
    const key = chatRunKey(conversationId);
    const existing = runs.get(key);
    if (existing) return existing;
    const run = createChatRun(key);
    runs.set(key, run);
    return run;
};

export const isChatRunPending = (runs: ChatRunMap, conversationId: unknown): boolean =>
    getChatRun(runs, conversationId)?.pending === true;

/**
 * Ends the in-flight attempt, keeping what outlives it (reconnect cursor,
 * replayable request, snapshot, reconnect affordance).
 */
export const finishChatRun = (runs: ChatRunMap, conversationId: unknown): void => {
    const run = getChatRun(runs, conversationId);
    if (!run) return;
    run.pending = false;
    run.requestId = null;
    run.abortController = null;
    run.abortCause = null;
    run.attemptAbortController = null;
    run.lastStreamActivity = null;
    run.reconnect = { runId: null, resumeFromChunk: 0 };
};

/** Ids of the conversations that are streaming right now, in insertion order. */
export const runningConversationIds = (runs: ChatRunMap): string[] => {
    const ids: string[] = [];
    runs.forEach((run, key) => {
        if (run.pending && key.length > 0) ids.push(key);
    });
    return ids;
};

/** True when `conversationId` is the conversation currently on screen. */
export const isDisplayedConversation = (
    conversationId: unknown,
    displayedConversationId: unknown
): boolean => chatRunKey(conversationId) === chatRunKey(displayedConversationId);

/**
 * Whether to surface "Deine aktuellen Arbeiten in diesem Prozess sind zunächst abgeschlossen".
 *
 * Completing a task is an await, and the user is free to move on while it runs. The notice is about
 * the conversation they *finished*, so it is only meaningful while that conversation is still the
 * one on screen — otherwise it interrupts unrelated work with a message about something they have
 * already left behind.
 *
 * It is not merely cosmetic: acknowledging the dialog runs `onCompleteTaskClosed`, which acts on the
 * conversation currently displayed — it drops that row from the sidebar and navigates to its
 * neighbour. Fired from a conversation the user has since left, it therefore closes the **wrong**
 * one. Hence the same `isDisplayed` guard the surrounding UI updates already use.
 *
 * `hasResult` is the completion response: a missing response is what marks terminal completion (an
 * empty worklist in a valid response can still be an in-progress state).
 */
export const shouldShowProcessCompletionDialog = (
    hasResult: boolean,
    isDisplayed: boolean
): boolean => !hasResult && isDisplayed;

/** Same members, order-insensitive — used to avoid pointless re-renders. */
export const sameConversationIds = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) return false;
    const others = new Set(b);
    return a.every(id => others.has(id));
};

/**
 * Is the conversation *on screen* running right now?
 *
 * The chat knows it — `ChatBot` holds both the open conversation and the set of
 * running ones — while the header that shows it (`SiteHeader`, variant `chat`)
 * lives in `AppShell`, one level *above* the chat, and therefore cannot read it
 * from there. Rather than threading a context through the shell for a single
 * boolean, the chat publishes it here and the header subscribes.
 *
 * Deliberately additive: nothing above this line changes. The value is a
 * session-wide singleton because exactly one chat page is on screen at a time;
 * the chat clears it when it unmounts. It is *not* "any conversation is
 * running" — a background run belongs in the sidebar, not in the header.
 */
let displayedConversationRunning = false;
const displayedRunListeners = new Set<() => void>();

/** Called by the chat whenever the displayed conversation starts/stops running. */
export const publishDisplayedConversationRun = (running: boolean): void => {
    if (displayedConversationRunning === running) return;
    displayedConversationRunning = running;
    displayedRunListeners.forEach(listener => listener());
};

/** `useSyncExternalStore` subscribe: returns the unsubscribe function. */
export const subscribeDisplayedConversationRun = (listener: () => void): (() => void) => {
    displayedRunListeners.add(listener);
    return () => {
        displayedRunListeners.delete(listener);
    };
};

export const getDisplayedConversationRun = (): boolean => displayedConversationRunning;
